import React from "react";
import { Button, Drawer, Tag } from "antd";
import { CheckOutlined } from "@ant-design/icons";

import {
  DASHBOARD_PRESETS,
  DashboardPreset,
  PRESET_GROUPS,
  PresetKey,
  paletteFor,
  tileSurface,
} from "../config/dashboardTheme";

const MONO_STACK =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/**
 * Miniature of the real dashboard, drawn with a preset's palette AND its style.
 *
 * Mirrors the actual page structure — quick action row, two headline metrics,
 * four smaller ones, the amount tiles and the ticket type tiles — at the same
 * proportions the flex layout gives them. It honours panel fill, outline vs
 * solid treatment, border weight, corners and label case, so a statement theme
 * previews as the restyle it is rather than as a recolour.
 */
const PresetThumbnail: React.FC<{
  preset: DashboardPreset;
  mode: "light" | "dark";
}> = ({ preset, mode }) => {
  const p = paletteFor(preset, mode);
  const s = preset.style;
  const r = Math.max(2, Math.round(preset.radius / 3));
  const bw = Math.max(1, Math.round(s.borderWidth * 0.75));
  const shadow = s.glow ? `0 0 8px ${s.glow}40` : "none";

  const panelBg =
    s.panelFill === "none"
      ? "transparent"
      : s.panelFill === "solid"
        ? p.panelBg
        : `linear-gradient(to bottom right, ${p.panelBg}, ${p.panelBgAlt})`;

  const panel: React.CSSProperties = {
    background: panelBg,
    border: `${bw}px solid ${p.panelBorder}`,
    borderRadius: r,
    boxShadow: shadow,
    padding: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minHeight: 0,
    overflow: "hidden",
  };

  const bar = (color: string, width: string, height: number) => (
    <div style={{ background: color, width, height, borderRadius: 1 }} />
  );

  const title = bar(p.heading, "34%", 3);

  const metric = (withIcon: boolean) => (
    <div
      style={{
        ...panel,
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
      }}
    >
      <div style={{ flex: 1, display: "grid", gap: 2 }}>
        {bar(p.muted, "60%", 2)}
        {bar(p.value, "42%", withIcon ? 5 : 4)}
      </div>
      {withIcon && (
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: s.tileStyle === "outline" ? "transparent" : p.iconBg,
            border: `${bw}px solid ${p.iconText}`,
            flexShrink: 0,
          }}
        />
      )}
    </div>
  );

  const tile = (bg: string, fg: string) => (
    <div
      style={{
        ...tileSurface(preset, bg, fg),
        flex: 1,
        borderRadius: Math.max(1, r - 1),
        boxShadow: shadow,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      <div style={{ background: fg, width: "42%", height: 2, borderRadius: 1 }} />
    </div>
  );

  const action = (color: string, key: number) => {
    const outline = s.buttonStyle === "outline";
    return (
      <div
        key={key}
        style={{
          flex: 1,
          background: outline ? "transparent" : color,
          border: `${bw}px solid ${color}`,
          borderRadius: Math.max(1, r - 1),
          boxShadow: shadow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <div
          style={{
            background: outline ? color : p.actionText,
            width: "55%",
            height: 2,
            borderRadius: 1,
          }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        background: p.pageBg,
        padding: 5,
        display: "flex",
        flexDirection: "column",
        gap: Math.max(3, Math.round(preset.gap / 3)),
        height: 150,
        fontFamily: s.numericFont === "mono" ? MONO_STACK : undefined,
      }}
    >
      {/* Quick actions */}
      <div style={{ ...panel, flex: 1.2 }}>
        {title}
        <div style={{ flex: 1, display: "flex", gap: 3, minHeight: 0 }}>
          {[
            p.actionEmerald,
            p.actionEmerald,
            p.actionEmerald,
            p.actionBlue,
            p.actionRed,
          ].map(action)}
        </div>
      </div>

      {/* Headline metrics */}
      <div style={{ flex: 1, display: "flex", gap: 3, minHeight: 0 }}>
        {metric(true)}
        {metric(true)}
      </div>

      {/* Secondary metrics */}
      <div style={{ flex: 1, display: "flex", gap: 3, minHeight: 0 }}>
        {metric(false)}
        {metric(false)}
        {metric(false)}
        {metric(false)}
      </div>

      {/* Tickets on screen */}
      <div style={{ ...panel, flex: 1.4 }}>
        {title}
        <div style={{ flex: 1, display: "flex", gap: 2, minHeight: 0 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <React.Fragment key={i}>
              {tile(p.amountBg, p.amountText)}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Ticket types */}
      <div style={{ ...panel, flex: 1 }}>
        <div style={{ flex: 1, display: "flex", gap: 3, minHeight: 0 }}>
          {tile(p.typeNewBg, p.typeNewText)}
          {tile(p.typeLuckyBg, p.typeLuckyText)}
          {tile(p.typeEndingBg, p.typeEndingText)}
        </div>
      </div>
    </div>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** the persisted choice */
  savedKey: PresetKey;
  /** what is currently showing on the dashboard (preview or saved) */
  selectedKey: PresetKey;
  /** which palette the previews show, driven by the app-wide light/dark toggle */
  mode: "light" | "dark";
  isSaving: boolean;
  /** live-preview a preset on the dashboard behind the drawer */
  onPreview: (key: PresetKey) => void;
  onSave: (key: PresetKey) => void;
  onReset: () => void;
}

/**
 * Appearance picker. Selecting a theme previews it live across the whole app
 * behind the drawer; Cancel reverts, Save stores it for the store.
 */
const ModalDashboardSettings: React.FC<Props> = ({
  open,
  onClose,
  savedKey,
  selectedKey,
  mode,
  isSaving,
  onPreview,
  onSave,
  onReset,
}) => (
  <Drawer
    title="Appearance"
    placement="right"
    width={560}
    open={open}
    onClose={onClose}
    bodyStyle={{ padding: 16 }}
    footer={
      <div style={{ textAlign: "right" }}>
        <Button onClick={onReset} style={{ marginRight: 8 }}>
          Reset
        </Button>
        <Button onClick={onClose} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={isSaving}
          onClick={() => onSave(selectedKey)}
        >
          Save
        </Button>
      </div>
    }
  >
    <p style={{ marginTop: 0, marginBottom: 20, opacity: 0.7, fontSize: 13 }}>
      Pick a theme — it previews across the whole app straight away, and Save
      keeps it for this store. Every theme keeps the same layout.
    </p>

    {PRESET_GROUPS.map((group) => (
      <section key={group.title} style={{ marginBottom: 26 }}>
        <h4
          style={{
            margin: "0 0 2px",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            opacity: 0.55,
          }}
        >
          {group.title}
        </h4>
        <p style={{ margin: "0 0 12px", fontSize: 12, opacity: 0.6 }}>
          {group.note}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {group.keys.map((key) => {
            const preset = DASHBOARD_PRESETS[key];
            const isSelected = key === selectedKey;

            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => onPreview(key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onPreview(key);
                }}
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  cursor: "pointer",
                  outline: isSelected
                    ? "2px solid #10B981"
                    : "1px solid rgba(128,128,128,0.35)",
                  outlineOffset: isSelected ? 1 : 0,
                }}
              >
                <PresetThumbnail preset={preset} mode={mode} />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {preset.label}
                  </span>
                  {key === savedKey && (
                    <Tag color="default" style={{ marginInlineEnd: 0 }}>
                      Saved
                    </Tag>
                  )}
                  {isSelected && (
                    <CheckOutlined
                      style={{ color: "#10B981", marginLeft: "auto" }}
                    />
                  )}
                </div>
                <div
                  style={{
                    padding: "0 10px 10px",
                    fontSize: 12,
                    opacity: 0.65,
                    lineHeight: 1.4,
                  }}
                >
                  {preset.description}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    ))}
  </Drawer>
);

export default ModalDashboardSettings;
